import { computed, ref } from 'vue';
import DebtKanbanApi from 'dashboard/api/debtKanban';

/**
 * Estado da tela de administração de funis e etapas (T3.5). Toda ESCRITA da
 * API devolve o FUNIL INTEIRO (funnels/show e stages/show renderizam a mesma
 * partial _funnel), e este composable substitui o objeto por inteiro — nunca
 * remonta a lista a partir da entidade tocada. Estado derivado reconstruído no
 * cliente foi exatamente o que reprovou a unidade do board.
 *
 * A lista carrega com include_archived=true: desarquivar é impossível se o
 * funil arquivado não aparecer em lugar nenhum.
 *
 * @returns {Object} estado e ações da administração de funis.
 */
export function useKanbanFunnelAdmin() {
  const funnels = ref([]);
  const isLoading = ref(false);
  const hasLoadError = ref(false);
  const isSubmitting = ref(false);
  const selectedFunnelId = ref(null);

  const selectedFunnel = computed(
    () =>
      funnels.value.find(funnel => funnel.id === selectedFunnelId.value) || null
  );

  const replaceFunnel = funnel => {
    const index = funnels.value.findIndex(item => item.id === funnel.id);
    if (index === -1) {
      funnels.value.push(funnel);
      return;
    }
    funnels.value.splice(index, 1, funnel);
  };

  const removeFunnel = funnelId => {
    const index = funnels.value.findIndex(item => item.id === funnelId);
    if (index !== -1) funnels.value.splice(index, 1);
    if (selectedFunnelId.value === funnelId) selectedFunnelId.value = null;
  };

  // Erro TRADUZIDO no cliente, e não a mensagem do corpo: o backend responde
  // em inglês (config/locales/en.yml, errors.kanban.*) e esta interface é
  // pt-BR. Mostrar o texto do servidor misturaria os dois idiomas na mesma
  // tela, que se lê pior do que qualquer um dos dois inteiro.
  const errorKeyForStatus = status => {
    if (status === 401 || status === 403) return 'FORBIDDEN';
    if (status === 404) return 'NOT_FOUND';
    if (status === 422) return 'INVALID';
    return 'GENERIC';
  };

  const failure = (error, overrides = {}) => ({
    ok: false,
    errorKey: errorKeyForStatus(error?.response?.status),
    cardsCount: error?.response?.data?.cards_count ?? null,
    ...overrides,
  });

  const load = async () => {
    isLoading.value = true;
    hasLoadError.value = false;
    try {
      const { data } = await DebtKanbanApi.funnels({ includeArchived: true });
      funnels.value = data;
      if (!selectedFunnel.value) {
        selectedFunnelId.value = data.length ? data[0].id : null;
      }
    } catch (error) {
      hasLoadError.value = true;
    } finally {
      isLoading.value = false;
    }
  };

  const selectFunnel = funnelId => {
    selectedFunnelId.value = funnelId;
  };

  // Envolve TODA escrita: um único `isSubmitting` (o diálogo bloqueia os
  // controles enquanto uma escrita corre) e um único ponto onde a resposta
  // vira estado, para nenhum caminho esquecer de substituir o funil.
  const submit = async (operation, onSuccess) => {
    if (isSubmitting.value) return { ok: false, errorKey: null };

    isSubmitting.value = true;
    try {
      const { data } = await operation();
      onSuccess(data);
      return { ok: true, funnel: data };
    } catch (error) {
      return failure(error);
    } finally {
      isSubmitting.value = false;
    }
  };

  // Funil novo nasce SEM etapa (decisão do controller: inventar etapas padrão
  // seria decidir pelo usuário o processo que ele veio configurar) e um funil
  // sem etapa é inerte — o board devolve zero colunas. Por isso ele já entra
  // selecionado: a tela conduz à criação da primeira etapa.
  const createFunnel = async name => {
    const result = await submit(
      () => DebtKanbanApi.createFunnel({ name }),
      data => {
        replaceFunnel(data);
        selectedFunnelId.value = data.id;
      }
    );
    return result;
  };

  const renameFunnel = (funnelId, name) =>
    submit(
      () => DebtKanbanApi.updateFunnel(funnelId, { name }),
      data => replaceFunnel(data)
    );

  const archiveFunnel = funnelId =>
    submit(
      () => DebtKanbanApi.archiveFunnel(funnelId),
      data => replaceFunnel(data)
    );

  const unarchiveFunnel = funnelId =>
    submit(
      () => DebtKanbanApi.unarchiveFunnel(funnelId),
      data => replaceFunnel(data)
    );

  // 409 aqui só tem um significado (funnel_has_cards) e a saída oferecida é
  // ARQUIVAR: `dependent: :destroy` apagaria todos os cards junto, e o card é
  // o único registro de onde a conversa está.
  const deleteFunnel = async funnelId => {
    if (isSubmitting.value) return { ok: false, errorKey: null };

    isSubmitting.value = true;
    try {
      await DebtKanbanApi.deleteFunnel(funnelId);
      removeFunnel(funnelId);
      return { ok: true };
    } catch (error) {
      if (error?.response?.status === 409) {
        return {
          ok: false,
          errorKey: 'FUNNEL_HAS_CARDS',
          cardsCount: error.response.data?.cards_count ?? null,
        };
      }
      return failure(error);
    } finally {
      isSubmitting.value = false;
    }
  };

  const createStage = (funnelId, attributes) =>
    submit(
      () => DebtKanbanApi.createStage(funnelId, attributes),
      data => replaceFunnel(data)
    );

  const updateStage = (funnelId, stageId, attributes) =>
    submit(
      () => DebtKanbanApi.updateStage(funnelId, stageId, attributes),
      data => replaceFunnel(data)
    );

  const reorderStages = (funnelId, stageIds) =>
    submit(
      () => DebtKanbanApi.reorderStages(funnelId, stageIds),
      data => replaceFunnel(data)
    );

  // Os três 409 de stages#destroy chegam com a MESMA forma no corpo
  // ({error, cards_count}) e sem código de máquina, então a distinção é feita
  // aqui com o mesmo predicado do servidor (stages_controller.rb
  // #destroy_locked_stage, nesta ordem):
  //   1. cards > 0 e o funil tem UMA etapa   -> stage_last_with_cards
  //   2. cards > 0 e nenhum destino enviado  -> stage_needs_target
  //   3. cards acima do teto de 500          -> stage_cards_limit_exceeded
  // Com destino válido enviado, (1) é inalcançável: um destino igual à própria
  // etapa é 422 e um de outro funil é 404, logo um funil de uma etapa só nunca
  // chega aqui com destino. Sobra (3).
  //
  // LIMITE: a classificação lê a contagem de etapas que ESTE cliente tem em
  // mãos. Se outro administrador acabou de criar/remover uma etapa deste
  // funil, a mensagem pode ser a do caso vizinho até a próxima carga — o que
  // removeria o acoplamento é um código de máquina no corpo do 409.
  const conflictKeyForStageDelete = ({ funnel, targetStageId }) => {
    if (targetStageId) return 'STAGE_CARDS_LIMIT_EXCEEDED';
    if ((funnel?.stages?.length ?? 0) <= 1) return 'STAGE_LAST_WITH_CARDS';
    return 'STAGE_NEEDS_TARGET';
  };

  const deleteStage = async (
    funnelId,
    stageId,
    { targetStageId = null } = {}
  ) => {
    if (isSubmitting.value) return { ok: false, errorKey: null };

    const funnel = funnels.value.find(item => item.id === funnelId);
    isSubmitting.value = true;
    try {
      const { data } = await DebtKanbanApi.deleteStage(funnelId, stageId, {
        targetStageId,
      });
      replaceFunnel(data);
      return { ok: true, funnel: data };
    } catch (error) {
      if (error?.response?.status === 409) {
        return {
          ok: false,
          errorKey: conflictKeyForStageDelete({ funnel, targetStageId }),
          cardsCount: error.response.data?.cards_count ?? null,
        };
      }
      return failure(error);
    } finally {
      isSubmitting.value = false;
    }
  };

  return {
    funnels,
    isLoading,
    hasLoadError,
    isSubmitting,
    selectedFunnelId,
    selectedFunnel,
    load,
    selectFunnel,
    createFunnel,
    renameFunnel,
    archiveFunnel,
    unarchiveFunnel,
    deleteFunnel,
    createStage,
    updateStage,
    deleteStage,
    reorderStages,
  };
}
